/**
 * Generic Google Tag Manager data layer utilities
 */

import { devLog } from "./dev-log";

/**
 * Pushes data to Google Tag Manager's dataLayer
 * @param data - The data object to push to dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push(data);
    devLog("📊 GTM DataLayer Event:", data);
  }
};

/**
 * Creates user-specific data for GTM
 * @param userId - User ID or null
 * @returns Data object to push to dataLayer
 */
export const createUserDataForGTM = (userId: string | null) => {
  return {
    user_id: userId,
  };
};

/**
 * Creates sign_up event data for GTM
 * @param signupMethod - The method used for signup/login
 * @returns Data object to push to dataLayer
 */
export const createSignUpEventForGTM = (signupMethod: string) => {
  return {
    event: "sign_up",
    signup_method: signupMethod,
  };
};

/**
 * Creates ecommerce event data for GTM
 * @param eventName - The ecommerce event name (e.g., "view_item", "add_to_cart", "purchase")
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param finalPrice - The final price for the item
 * @param productId - Product ID
 * @param productName - Product name
 * @param skuId - The SKU ID for the specific variant
 * @returns Data object to push to dataLayer for ecommerce events
 */
export const createEcommerceEventForGTM = (
  eventName: string,
  currency: string,
  finalPriceInCent: number,
  productId: string,
  productName: string,
  skuId: string
) => {
  return {
    event: eventName,
    ecommerce: {
      currency,
      value: finalPriceInCent / 100,
      items: [
        {
          item_id: productId,
          item_name: productName,
          item_variant: skuId,
          price: finalPriceInCent / 100,
        },
      ],
    },
  };
};

/**
 * Creates start_trial event data for GTM
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param trialDuration - Duration of trial in days as string (e.g., "1d", "2d", "30d") (optional)
 * @returns Data object to push to dataLayer for start_trial events
 */
export const createStartTrialEventForGTM = (currency: string, trialDuration?: string) => {
  const eventData: any = {
    event: "start_trial",
    subscription_product_id: "blidz_plus_trial",
    subscription_price: 0,
    subscription_currency: currency,
  };

  if (trialDuration !== undefined) {
    eventData.trial_duration = trialDuration;
  }

  return eventData;
};

/**
 * Creates authenticated event data for GTM
 * @returns Data object to push to dataLayer for authenticated events
 */
export const createAuthenticatedEventForGTM = () => {
  return {
    event: "authenticated",
  };
};

/**
 * Creates React Router navigation event data for GTM
 * @param navigationType - Type of navigation ("browser_back_forward" or "react_router")
 * @param pathname - Current pathname
 * @param search - Search parameters
 * @param hash - URL hash
 * @param state - History state (for browser navigation)
 * @returns Data object to push to dataLayer for React Router navigation events
 */
export const createNavigationEventForGTM = (
  navigationType: "browser_history_change" | "react_router_navigation",
  pathname: string,
  search?: string,
  hash?: string,
  state?: any
) => {
  const eventData: Record<string, any> = {
    event: "page_navigation",
    navigationType,
    pageLocation: `${pathname}${search || ""}${hash || ""}`,
    pagePath: pathname,
  };

  if (search) {
    eventData.pageSearch = search;
  }

  if (hash) {
    eventData.pageHash = hash;
  }

  if (state && navigationType === "react_router_navigation") {
    eventData.navigationState = JSON.stringify(state);
  }

  return eventData;
};

/**
 * Creates payment state change event data for GTM
 * @param currentState - The current payment state
 * @param previousState - The previous payment state (if available)
 * @param phase - Whether this is the beginning or end of the state
 * @param additionalData - Any additional data to include in the event
 * @returns Data object to push to dataLayer for payment state change events
 */
export const createPaymentStateChangeEventForGTM = (
  currentState: string,
  previousState: string | null,
  phase: "begin" | "end",
  additionalData?: Record<string, any>
) => {
  const eventData: Record<string, any> = {
    event: "payment_state_change",
    payment_state: currentState,
    state_phase: phase,
    timestamp: new Date().toISOString(),
  };

  if (previousState) {
    eventData.previous_state = previousState;
  }

  if (additionalData) {
    eventData.additional_data = additionalData;
  }

  return eventData;
};
